import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2Kpi,
  V2MetricStrip,
  LoadingState,
  StatePanel,
  type V2GridColumn,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa, journalHref, type AmountDisplayUnit } from '../../lib/finance-display.js';
import type { SalesReturnListResponse, SalesReturnRecord } from '../../lib/types/finance.js';
import { SalesReturnCreateModal } from './SalesReturnCreateModal.js';
import { PartyLabel } from './PartyLabel.js';

interface SalesReturnRow extends SalesReturnRecord {
  readonly id: string;
}

export function OpenItemsSalesReturnsTab({
  slug,
  base,
  unit,
  unitLabel,
}: {
  readonly slug: string;
  readonly base: string;
  readonly unit: AmountDisplayUnit;
  readonly unitLabel: string;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItemForView, setSelectedItemForView] = useState<SalesReturnRecord | null>(null);

  const returns = useApiGet<SalesReturnListResponse>(`${base}/api/v1/finance/ar/sales-returns`, [
    slug,
    refreshKey,
  ]);

  const rows: SalesReturnRow[] = useMemo(() => {
    if (returns.status !== 'ready') return [];
    return returns.data.items.map((item) => ({ ...item, id: item.salesReturnId }));
  }, [returns]);

  // Totals for KPIs
  const totals = useMemo(() => {
    let count = 0;
    let totalGross = 0n;
    let totalVat = 0n;
    let totalAmount = 0n;

    for (const r of rows) {
      count++;
      totalGross += BigInt(r.grossAmountRial || '0');
      totalVat += BigInt(r.vatAmountRial || '0');
      totalAmount += BigInt(r.totalAmountRial || '0');
    }

    return { count, totalGross, totalVat, totalAmount };
  }, [rows]);

  const columns: readonly V2GridColumn<SalesReturnRow>[] = [
    {
      key: 'code',
      header: 'شماره اعلامیه',
      render: (row) => (
        <span
          className="ltr sk-num font-mono"
          style={{ fontWeight: 600, color: 'var(--sk-color-primary, #2563eb)', cursor: 'pointer' }}
          onClick={() => setSelectedItemForView(row)}
        >
          {row.code}
        </span>
      ),
    },
    {
      key: 'party',
      header: 'مشتری',
      render: (row) => (
        <PartyLabel
          slug={slug}
          partyId={row.partyId}
          href={`${base}/master-data/parties/${row.partyId}`}
        />
      ),
    },
    {
      key: 'invoice',
      header: 'فاکتور مبدأ',
      render: (row) => {
        if (!row.originalInvoiceReference && !row.originalOpenItemId) {
          return <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>;
        }
        return (
          <span className="ltr sk-num font-mono">
            {row.originalInvoiceReference || 'فاکتور باز'}
          </span>
        );
      },
    },
    {
      key: 'date',
      header: 'تاریخ',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>,
    },
    {
      key: 'reason',
      header: 'علت مرجوعی',
      render: (row) => (
        <span
          style={{
            fontSize: 12,
            maxWidth: 220,
            display: 'inline-block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.returnReason}
        >
          {row.returnReason}
        </span>
      ),
    },
    {
      key: 'gross',
      header: `مبلغ ناخالص (${unitLabel})`,
      render: (row) => (
        <span className="ltr sk-num font-mono">{formatRialFa(row.grossAmountRial, unit)}</span>
      ),
    },
    {
      key: 'vat',
      header: `مالیات (${unitLabel})`,
      render: (row) => (
        <span className="ltr sk-num font-mono">{formatRialFa(row.vatAmountRial, unit)}</span>
      ),
    },
    {
      key: 'total',
      header: `مبلغ کل بستانکار (${unitLabel})`,
      render: (row) => (
        <span
          className="ltr sk-num font-mono"
          style={{ fontWeight: 700, color: 'var(--sk-color-success, #16a34a)' }}
        >
          {formatRialFa(row.totalAmountRial, unit)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge
          tone={
            row.status === 'posted' ? 'success' : row.status === 'cancelled' ? 'danger' : 'neutral'
          }
        >
          {row.status === 'posted'
            ? 'ثبت قطعی'
            : row.status === 'cancelled'
              ? 'باطل‌شده'
              : 'پیش‌نویس'}
        </V2Badge>
      ),
    },
    {
      key: 'journal',
      header: 'سند مالی',
      render: (row) => {
        const jId = row.originatingJournalId || row.journalId;
        if (!jId) return <span>—</span>;
        const display = row.journalNumber
          ? row.journalNumber.startsWith('JRN')
            ? row.journalNumber
            : `JRN-${row.journalNumber}`
          : 'مشاهده سند';
        return (
          <Link
            to={journalHref(base, jId)}
            className="ltr font-mono"
            style={{ fontSize: 12, color: 'var(--sk-color-primary, #2563eb)' }}
          >
            {display}
          </Link>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <V2Button variant="ghost" size="sm" onClick={() => setSelectedItemForView(row)}>
          اقلام
        </V2Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Action header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            برگشت از فروش و اعلامیه‌های بستانکار (Credit Memos)
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            مدیریت کالاهای مرجوعی، تعدیل حساب دریافتنی مشتری و صدور سند حسابداری دوبل.
          </p>
        </div>
        <V2Button variant="primary" icon="plus" size="sm" onClick={() => setShowCreateModal(true)}>
          ثبت برگشت از فروش
        </V2Button>
      </div>

      {/* KPI metrics strip */}
      <V2MetricStrip>
        <V2Kpi label="تعداد اعلامیه‌ها" value={totals.count.toLocaleString('fa-IR')} />
        <V2Kpi
          label="جمع ناخالص مرجوعی"
          value={`${formatRialFa(totals.totalGross.toString(), unit)} ${unitLabel}`}
        />
        <V2Kpi
          label="جمع مالیات تعدیل‌شده"
          value={`${formatRialFa(totals.totalVat.toString(), unit)} ${unitLabel}`}
        />
        <V2Kpi
          label="کل مبلغ بستانکارشده به مشتریان"
          value={`${formatRialFa(totals.totalAmount.toString(), unit)} ${unitLabel}`}
        />
      </V2MetricStrip>

      {/* Grid state */}
      {returns.status === 'loading' ? <LoadingState /> : null}
      {returns.status === 'error' ? (
        <StatePanel
          variant={returns.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            returns.error.status === 403 ? 'دسترسی ندارید' : 'فهرست مرجوعی‌ها قابل دریافت نیست'
          }
        />
      ) : null}
      {returns.status === 'ready' ? (
        <V2DataGrid
          icon="invoice"
          title="فهرست اعلامیه‌های برگشت از فروش"
          subtitle={`${rows.length.toLocaleString('fa-IR')} اعلامیه`}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          footerLabel={
            <>
              جمع کل ·{' '}
              <span className="sk-num font-mono">{rows.length.toLocaleString('fa-IR')}</span>{' '}
              اعلامیه بستانکار
            </>
          }
          zebra
          emptyState={
            <V2EmptyState
              icon="info"
              title="هیچ اعلامیه برگشت از فروشی ثبت نشده است"
              description="در صورت مرجوع شدن کالا توسط مشتری، می‌توانید از دکمه «ثبت برگشت از فروش» استفاده کنید."
            />
          }
        />
      ) : null}

      {/* Create Modal */}
      <SalesReturnCreateModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
        slug={slug}
        base={base}
        unit={unit}
        unitLabel={unitLabel}
      />

      {/* View Details Modal */}
      {selectedItemForView && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedItemForView(null);
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 720,
              maxHeight: '85vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              border: '1px solid var(--sk-border)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setSelectedItemForView(null)} />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--sk-divider)',
                paddingBottom: 10,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  اعلامیه برگشت از فروش شماره {selectedItemForView.code}
                </h3>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  تاریخ: {displayJalaliDate(selectedItemForView.businessDate)} · علت:{' '}
                  {selectedItemForView.returnReason}
                </span>
              </div>
              <V2Button variant="ghost" size="sm" onClick={() => setSelectedItemForView(null)}>
                بستن
              </V2Button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                fontSize: 13,
                background: 'var(--sk-bg-subtle, rgba(0,0,0,0.02))',
                padding: 12,
                borderRadius: 8,
              }}
            >
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>مشتری: </span>
                <b>{selectedItemForView.partyName || selectedItemForView.partyId}</b>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>فاکتور مبدأ: </span>
                <span className="ltr font-mono">
                  {selectedItemForView.originalInvoiceReference || '—'}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>بازگشت به انبار: </span>
                <b>{selectedItemForView.restock ? 'بله' : 'خیر'}</b>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>جمع ناخالص: </span>
                <span className="ltr font-mono">
                  {formatRialFa(selectedItemForView.grossAmountRial, unit)} {unitLabel}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>مالیات: </span>
                <span className="ltr font-mono">
                  {formatRialFa(selectedItemForView.vatAmountRial, unit)} {unitLabel}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>کل بستانکار: </span>
                <span
                  className="ltr font-mono"
                  style={{ fontWeight: 700, color: 'var(--sk-color-success, #16a34a)' }}
                >
                  {formatRialFa(selectedItemForView.totalAmountRial, unit)} {unitLabel}
                </span>
              </div>
            </div>

            {selectedItemForView.notes && (
              <div
                style={{
                  fontSize: 13,
                  background: 'var(--sk-bg-subtle, rgba(0,0,0,0.03))',
                  padding: '8px 12px',
                  borderRadius: 6,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>توضیحات: </span>
                <span>{selectedItemForView.notes}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>اقلام مرجوعی</span>
              {(selectedItemForView.originatingJournalId || selectedItemForView.journalId) && (
                <Link
                  to={journalHref(
                    base,
                    selectedItemForView.originatingJournalId || selectedItemForView.journalId!,
                  )}
                  style={{ fontSize: 13, color: 'var(--sk-color-primary, #2563eb)' }}
                >
                  {selectedItemForView.journalNumber
                    ? `مشاهده سند حسابداری (${selectedItemForView.journalNumber}) ←`
                    : 'مشاهده سند حسابداری دوبل ←'}
                </Link>
              )}
            </div>

            {/* Lines table if available */}
            {selectedItemForView.lines && selectedItemForView.lines.length > 0 ? (
              <table className="sk-table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center' }}>#</th>
                    <th>شرح</th>
                    <th style={{ width: 80 }}>تعداد</th>
                    <th style={{ width: 120 }}>فی ({unitLabel})</th>
                    <th style={{ width: 110 }}>مالیات ({unitLabel})</th>
                    <th style={{ width: 120 }}>جمع ({unitLabel})</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemForView.lines.map((line) => (
                    <tr key={line.rowNumber}>
                      <td style={{ textAlign: 'center' }}>{line.rowNumber}</td>
                      <td>{line.description}</td>
                      <td className="ltr sk-num">{line.quantity}</td>
                      <td className="ltr sk-num font-mono">
                        {formatRialFa(line.unitPriceRial, unit)}
                      </td>
                      <td className="ltr sk-num font-mono">
                        {formatRialFa(line.vatAmountRial || '0', unit)}
                      </td>
                      <td className="ltr sk-num font-mono" style={{ fontWeight: 600 }}>
                        {formatRialFa(line.lineTotalRial || '0', unit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--sk-fg-muted)',
                  textAlign: 'center',
                  padding: 16,
                }}
              >
                اقلام تفصیلی در سند ثبت شده‌اند.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
